import { useState } from "react";
import { channels } from "@/mocks/guaData";
import SectionHeading from "./SectionHeading";

const SUBMIT_URL = "https://readdy.ai/api/form/daftttrep053ijub1vd0";

const quickHelp = [
  { icon: "ri-mail-line", label: "官方投稿邮箱", value: "fan@guaguachigua.cn" },
  { icon: "ri-chat-1-line", label: "官方微信", value: "guaguachigua" },
  { icon: "ri-flashlight-line", label: "投稿须知", value: "只收公开幽默内容，请勿投递隐私与争议信息" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：蜜罐非空则假装成功，不提交
    const hp = (fd.get("contact_alt") as string)?.trim() ?? "";
    if (hp) {
      setStatus("success");
      setMessage("投稿已收到，审核通过后会尽快收录并署上你的昵称，谢谢支持！");
      form.reset();
      return;
    }

    fd.delete("contact_alt");

    setStatus("idle");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, string>;
      const serverMsg = meta.message || parsed?.message || meta.detail || responseText;
      const isSpam = String(serverMsg).includes("spam") || String(responseText).includes("spam");

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("投稿已收到，审核通过后会尽快收录并署上你的昵称，谢谢支持！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          isSpam || !serverMsg
            ? "抱歉，提交未成功，内容可能含违规或敏感信息，请调整后重试。"
            : `提交失败：${serverMsg}`
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          {/* 左侧信息 */}
          <div>
            <SectionHeading
              kicker="投稿吃瓜 · 联系呱呱"
              title="有好瓜，别藏着，投给我们"
              desc="无论你是发现了神级神回复、刷到了名场面，还是单纯想给我们提意见，都欢迎在这里留言。记得留下昵称，上榜时好署名。"
            />
            <ul className="mt-8 space-y-4">
              {quickHelp.map((q) => (
                <li
                  key={q.label}
                  className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-100/60 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                    <i className={q.icon}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{q.label}</p>
                    <p className="text-sm font-medium text-foreground-800">{q.value}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl border border-secondary-200 bg-secondary-100 p-4 text-sm leading-relaxed text-secondary-900">
              <p className="flex items-start gap-2">
                <i className="ri-information-line mt-0.5 text-lg"></i>
                投稿即代表你确认内容来自公开渠道、可公开传播，且不侵犯他人权益。我们坚持理性吃瓜、克制表达。
              </p>
            </div>
          </div>

          {/* 右侧表单 */}
          <div className="rounded-3xl border border-background-200 bg-background-100/70 p-6 md:p-8">
            <form
              id="投稿表单"
              data-readdy-form="呱呱吃瓜投稿留言"
              onSubmit={onSubmit}
              className="space-y-4"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-700">
                    你的昵称 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    required
                    maxLength={20}
                    placeholder="上榜要署这个名"
                    className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">
                    联系邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收收录通知"
                    className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="channel" className="mb-1.5 block text-sm text-foreground-700">
                  投稿频道 <span className="text-accent-500">*</span>
                </label>
                <select
                  id="channel"
                  name="channel"
                  required
                  defaultValue=""
                  className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
                >
                  <option value="" disabled>
                    请选择要投稿到的频道
                  </option>
                  {channels.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  <option value="其他">其他 / 综合</option>
                </select>
              </div>

              <div>
                <label htmlFor="content" className="mb-1.5 block text-sm text-foreground-700">
                  投稿内容 / 神评或名场面描述 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="写清神评原文、出处或链接，越具体越容易收录哦～（最多500字）"
                  className="w-full resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm leading-relaxed outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
              </div>

              {/* 反垃圾隐藏字段 */}
              <div className="fp-protect" aria-hidden="true">
                <label htmlFor="contact_alt">请勿填写此项</label>
                <input
                  type="text"
                  id="contact_alt"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "success" && (
                <p className="flex items-center gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-close-circle-fill text-lg"></i>
                  {message}
                </p>
              )}

              <button
                type="submit"
                className="w-full whitespace-nowrap rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500"
              >
                提交投稿
              </button>
              <p className="text-center text-xs text-foreground-500">
                提交即代表同意我们的内容收录规则，感谢你为呱呱瓜田添砖加瓦。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}